import { Box, ButtonBase, IconButton, Stack, Typography } from '@mui/material'
import type { CatalogCardModel } from '@commerce/core'
import { YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'
import { ScrollRow } from '../design'

interface Props {
  models: CatalogCardModel[]
  /**
   * What the row is. It is a prop because the product page now draws two of
   * these from two different fields — "instead of this" (`alternativeProducts`)
   * and "with this" (`frequentlyBoughtTogether`) — and a row whose heading was
   * baked in could only ever be the first one.
   */
  title?: string
  onOpen: (sku: string) => void
  onAdd: (sku: string) => void
}

/**
 * A row of related products.
 *
 * The card is deliberately not `CatalogCard`: the design draws a narrower thing
 * here — 128px, one image band, name, price and a single round add button — and
 * pulling the grid card in at a smaller size would make every future change to
 * the grid a change to this row too.
 *
 * Opening and adding are two sibling buttons rather than a card-wide button with
 * a button inside it, which is invalid DOM and which React reports. The image
 * carries an empty `alt` because the name sits inside the same control and would
 * otherwise be announced twice.
 */
export const SimilarProducts = ({
  models,
  title = 'Productos similares',
  onOpen,
  onAdd,
}: Props) => {
  if (models.length === 0) return null

  return (
    <Box component="section" aria-label={title} sx={{ py: 1 }}>
      <Typography
        variant="overline"
        component="h2"
        sx={{ px: 2, display: 'block', color: YALO_SEMANTIC.fg3 }}
      >
        {title}
      </Typography>

      <ScrollRow>
        {models.map((model) => (
          <Box
            key={model.sku}
            sx={{
              width: 128,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: YALO_SEMANTIC.bgSurface,
              border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
              borderRadius: `${YALO_RADIUS.lg}px`,
              overflow: 'hidden',
            }}
          >
            <ButtonBase
              onClick={() => onOpen(model.sku)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                textAlign: 'left',
                flex: 1,
              }}
            >
              <Box
                sx={{
                  height: 72,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: YALO_SEMANTIC.bgSurface,
                  borderBottom: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
                }}
              >
                <Box
                  component="img"
                  src={model.imageUrl}
                  alt=""
                  sx={{ maxWidth: '68%', maxHeight: '80%', objectFit: 'contain' }}
                />
              </Box>
              <Typography
                sx={{
                  px: 1.25,
                  pt: 1,
                  pb: 0.5,
                  fontSize: 11.5,
                  fontWeight: 600,
                  lineHeight: '14px',
                  minHeight: 28,
                }}
              >
                {model.title}
              </Typography>
            </ButtonBase>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent={model.showPrice ? 'space-between' : 'flex-end'}
              sx={{ px: 1.25, pb: 1.25 }}
            >
              {model.showPrice && (
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'primary.dark' }}>
                  {model.priceText}
                </Typography>
              )}
              <IconButton
                aria-label={`Agregar ${model.title}`}
                disabled={!model.canAddToCart}
                onClick={() => onAdd(model.sku)}
                sx={{
                  width: 26,
                  height: 26,
                  fontSize: 14,
                  fontWeight: 700,
                  borderRadius: `${YALO_RADIUS.pill}px`,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                +
              </IconButton>
            </Stack>
          </Box>
        ))}
      </ScrollRow>
    </Box>
  )
}
